import React, { useEffect, useRef, useState } from "react";
import { Upload, Link2, ImageOff } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { api, errorMessage, mediaUrl } from "../../api";

export const Field = ({ label, hint, children }) => (
  <label className="block text-sm font-medium">
    <span className="flex items-baseline justify-between">
      {label} {hint && <span className="text-xs font-normal text-[var(--fr-muted)]">{hint}</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export const ImagePicker = ({ label, value, onChange, aspect = "aspect-[16/10]", fit = "object-cover", hint, testid }) => {
  const fileRef = useRef(null);
  const [mode, setMode] = useState("upload");
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(errorMessage(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="text-sm font-medium">{label}</div>
      <div className={`${aspect} rounded-xl overflow-hidden bg-[var(--fr-bg-alt)] flex items-center justify-center text-[var(--fr-muted)]`} data-testid={`${testid}-preview`}>
        {value ? <img src={mediaUrl(value)} alt="" className={`w-full h-full ${fit} p-2`} onError={(e) => (e.currentTarget.style.display = "none")} /> : <ImageOff size={22} />}
      </div>
      <div className="flex gap-1 bg-[var(--fr-bg-alt)] p-1 rounded-xl">
        {[
          ["upload", Upload, "Upload"],
          ["url", Link2, "Paste URL"],
        ].map(([m, Icon, l]) => (
          <button key={m} type="button" onClick={() => setMode(m)} className={`flex-1 h-8 rounded-lg text-xs font-medium inline-flex items-center justify-center gap-1.5 ${mode === m ? "bg-white shadow-sm" : "text-[var(--fr-muted)]"}`} data-testid={`${testid}-mode-${m}`}>
            <Icon size={13} /> {l}
          </button>
        ))}
      </div>
      {mode === "upload" ? (
        <>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-file`} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn btn-outline btn-sm w-full disabled:opacity-60" data-testid={`${testid}-upload`}>
            <Upload size={14} className="mr-1.5" /> {uploading ? "Uploading…" : value ? "Replace image" : "Choose image"}
          </button>
          {hint && <p className="text-xs text-[var(--fr-muted)]">{hint}</p>}
        </>
      ) : (
        <input className="fr-input !h-10 text-sm" placeholder="https://…/image.jpg" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-url`} />
      )}
    </div>
  );
};

export const useAdminRecord = (path, id, empty) => {
  const isNew = !id || id === "new";
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  useEffect(() => {
    if (isNew) return;
    api
      .get(`${path}/${id}`)
      .then((r) => setForm({ ...empty, ...r.data }))
      .catch((e) => toast.error(errorMessage(e)))
      .finally(() => setLoading(false));
  }, [path, id, isNew]); // eslint-disable-line react-hooks/exhaustive-deps
  return { form, setForm, loading, isNew };
};
